import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as queryStatusesApi from './query-statuses.api';

import type {
  CreateQueryStatusRequest,
  QueryStatusListQuery,
  QueryStatusOptionsQuery,
  UpdateQueryStatusRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: QueryStatusListQuery) => ['query-statuses', 'list', query] as const,
  detail: (id: string) => ['query-statuses', 'detail', id] as const,
  options: (query?: QueryStatusOptionsQuery) => ['query-statuses', 'options', query] as const,
};

export const useQueryStatusesList = (query: QueryStatusListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => queryStatusesApi.listQueryStatuses(query),
    staleTime: 30_000,
  });

export const useQueryStatusOptions = (query?: QueryStatusOptionsQuery) =>
  useQuery({
    queryKey: QK.options(query),
    queryFn: () => queryStatusesApi.listQueryStatusOptions(query),
    staleTime: 60_000,
  });

/**
 * Cross-cache invalidation for status mutations. Leads embed
 * `currentStatus` (id, code, name, bucket, color) on every list + detail row
 * - when a master's color or name changes, the embedded copy on cached lead
 * rows goes stale. Invalidating both keeps the colored stage indicators in
 * lockstep with the master. See ADR-0032 §18.
 */
const invalidateStatusFanout = (qc: ReturnType<typeof useQueryClient>): void => {
  qc.invalidateQueries({ queryKey: ['query-statuses', 'list'] });
  qc.invalidateQueries({ queryKey: ['query-statuses', 'options'] });
  qc.invalidateQueries({ queryKey: ['leads', 'list'] });
  qc.invalidateQueries({ queryKey: ['leads', 'detail'] });
};

export const useCreateQueryStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateQueryStatusRequest) => queryStatusesApi.createQueryStatus(payload),
    onSuccess: (qs) => {
      invalidateStatusFanout(qc);
      toast.success(`Added ${qs.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add query status'),
  });
};

export const useUpdateQueryStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateQueryStatusRequest }) =>
      queryStatusesApi.updateQueryStatus(id, payload),
    onSuccess: (qs) => {
      invalidateStatusFanout(qc);
      qc.invalidateQueries({ queryKey: QK.detail(qs.id) });
      toast.success('Query status updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update query status'),
  });
};

export const useDeleteQueryStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => queryStatusesApi.deleteQueryStatus(id),
    onSuccess: () => {
      invalidateStatusFanout(qc);
      toast.success('Query status deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete query status'),
  });
};

export const useBulkDeleteQueryStatuses = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => queryStatusesApi.bulkDeleteQueryStatuses(ids),
    onSuccess: () => invalidateStatusFanout(qc),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to delete query statuses'),
  });
};

export const useBulkSetQueryStatusesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      queryStatusesApi.bulkSetQueryStatusesStatus(ids, isActive),
    onSuccess: () => invalidateStatusFanout(qc),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update query status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
